<template>
  <div class="transportistas">
    <h2 class="subheading grey--text">Transportistas</h2>
    <v-container class="my-5">
      <v-layout row wrap class="mb-3" align-center>
        <v-spacer></v-spacer>
        <v-flex shrink>
          <v-btn color="primary" dark @click="openCreateDialog" :disabled="!providerCompanyId">
            <v-icon left>add</v-icon>
            Nuevo Transportista
          </v-btn>
        </v-flex>
      </v-layout>

      <v-alert v-if="!providerHasParkingModule" type="warning" :value="true">
        Tu empresa no tiene habilitado el modulo de parking para empresas host asociadas.
      </v-alert>

      <v-card v-else class="elevation-2">
        <v-card-title>
          Lista de Transportistas
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Buscar"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="transportistas"
          :search="search"
          :loading="loading"
          class="elevation-1"
          rows-per-page-text="Filas por pagina"
          :rows-per-page-items="[
            { text: '5', value: 5 },
            { text: '10', value: 10 },
            { text: '25', value: 25 },
            { text: 'Todos', value: -1 }
          ]"
          no-data-text="No hay transportistas registrados"
        >
          <template v-slot:items="props">
            <td>{{ props.item.first_name }}</td>
            <td>{{ props.item.last_name }}</td>
            <td>{{ props.item.telephone }}</td>
            <td>{{ props.item.plates }}</td>
            <td>{{ props.item.unit_specs }}</td>
            <td>
              <v-btn small icon color="primary" @click="openEditDialog(props.item)">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn small icon color="error" @click="openDeleteDialog(props.item)">
                <v-icon small>delete</v-icon>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dialogForm" persistent max-width="700px">
        <v-form @submit.prevent="saveTransportista">
          <v-card class="elevation-5">
            <v-toolbar dark color="primary">
              <v-toolbar-title>{{ isEditing ? 'Editar Transportista' : 'Nuevo Transportista' }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                class="align-self-end"
                color="red"
                icon
                @click="closeDialog"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-3">
              <!-- SECCIÓN: DATOS PERSONALES -->
              <v-subheader class="primary--text font-weight-bold pl-0">
                <v-icon small class="mr-2" color="primary">person</v-icon>
                Datos personales
              </v-subheader>
              <v-divider class="mb-3"></v-divider>
              <v-layout row wrap>
                <v-flex xs12 sm6>
                  <v-text-field
                    class="px-2"
                    v-model="form.first_name"
                    label="Nombre *"
                    prepend-icon="person"
                    :error-messages="errors.first_name"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    class="px-2"
                    v-model="form.last_name"
                    label="Apellido *"
                    prepend-icon="person_outline"
                    :error-messages="errors.last_name"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <div class="px-2">
                    <v-label class="caption">Teléfono (+52)</v-label>
                    <vue-tel-input
                      class="mt-1"
                      v-model="form.telephone"
                      default-country="mx"
                      :preferred-countries="['mx']"
                      @input="clearTelephoneError"
                    ></vue-tel-input>
                    <v-alert :value="errors.telephone" color="error" icon="warning" outline small class="mt-2">
                      {{ errors.telephone }}
                    </v-alert>
                  </div>
                </v-flex>
              </v-layout>

              <v-subheader class="primary--text font-weight-bold pl-0 mt-2">
                <v-icon small class="mr-2" color="primary">local_shipping</v-icon>
                Datos del vehículo
              </v-subheader>
              <v-divider class="mb-3"></v-divider>
              <v-layout row wrap>
                <v-flex xs12 sm6>
                  <v-text-field
                    class="px-2"
                    v-model="form.plates"
                    label="Placas *"
                    prepend-icon="directions_car"
                    :error-messages="errors.plates"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field
                    class="px-2"
                    v-model="form.unit_specs"
                    label="Tipo de unidad"
                    prepend-icon="local_shipping"
                    :error-messages="errors.unit_specs"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-subheader class="primary--text font-weight-bold pl-0 mt-2">
                <v-icon small class="mr-2" color="primary">badge</v-icon>
                Identificación oficial
              </v-subheader>
              <v-divider class="mb-3"></v-divider>
              <input
                ref="ineInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onInePicked"
              />
              <v-layout row wrap align-center>
                <v-flex xs12 sm7>
                  <div
                    v-if="!inePreview"
                    class="ine-upload-zone"
                    @click="pickIne"
                  >
                    <v-icon size="36" color="primary lighten-1">add_photo_alternate</v-icon>
                    <div class="body-2 primary--text mt-1">Seleccionar imagen</div>
                    <div class="caption grey--text">Haz clic para buscar el archivo</div>
                  </div>
                  <v-card v-else class="ine-preview-card" @click="pickIne">
                    <v-img :src="inePreview" max-height="170" contain class="ine-preview-img"></v-img>
                    <div class="ine-preview-overlay">
                      <v-icon color="white" size="28">edit</v-icon>
                      <span class="caption white--text ml-1">Cambiar imagen</span>
                    </div>
                  </v-card>
                  <div v-if="errors.ine" class="error--text caption mt-1">{{ errors.ine }}</div>
                </v-flex>
                <v-flex xs12 sm5 class="pl-3" align-self-center>
                  <v-btn
                    v-if="inePreview"
                    small
                    outline
                    color="primary"
                    class="mb-2"
                    @click="pickIne"
                  >
                    <v-icon left small>crop</v-icon>
                    Cambiar imagen
                  </v-btn>
                  <br v-if="inePreview" />
                  <v-btn
                    v-if="inePreview"
                    small
                    flat
                    color="grey darken-1"
                    @click="clearIne"
                  >
                    <v-icon left small>delete_outline</v-icon>
                    Limpiar
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="px-4 py-3">
              <v-btn flat color="grey darken-1" @click="closeDialog">
                <v-icon left>cancel</v-icon>
                Cancelar
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" type="submit" :loading="saving">
                <v-icon left>{{ isEditing ? 'save' : 'person_add' }}</v-icon>
                {{ isEditing ? 'Guardar cambios' : 'Crear transportista' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>

      <v-dialog v-model="dialogCropIne" persistent scrollable max-width="760px">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title>Recortar identificación oficial</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <clipper-fixed
              v-if="imageIneURL"
              ref="clipperIne"
              :key="imageIneURL"
              :rotate="cropDegrees"
              :ratio="cropRatio"
              :src="imageIneURL"
              class="transportista-clipper"
            >
            </clipper-fixed>
            <v-alert v-else type="warning" outlined class="mb-0">
              No se pudo cargar la imagen. Intenta seleccionar el archivo nuevamente.
            </v-alert>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-layout row wrap class="justify-center">
              <v-flex xs12>
                <v-radio-group
                  class="justify-center"
                  v-model="cropRatio"
                  name="cropRatio"
                  row
                  label="Ratio"
                  :mandatory="false"
                >
                  <v-radio label="1:1" :value="1"></v-radio>
                  <v-radio label="3:2" :value="1.5"></v-radio>
                  <v-radio label="4:3" :value="1.3333333333333333"></v-radio>
                  <v-radio label="16:9" :value="1.7777777777777777"></v-radio>
                </v-radio-group>
              </v-flex>
              <v-flex xs12>
                <v-slider
                  class="justify-center mr-3"
                  v-model="cropDegrees"
                  label="Grados"
                  step="1"
                  max="180"
                  min="-180"
                  thumb-label="always"
                ></v-slider>
              </v-flex>
              <v-btn text class="mr-2" @click="cancelCropIne">Cancelar</v-btn>
              <v-btn color="primary" @click="applyIneCrop">Recortar</v-btn>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogDelete" max-width="420px">
        <v-card>
          <v-card-title class="headline">Eliminar transportista</v-card-title>
          <v-card-text>
            Se eliminara a {{ selectedItem.first_name }} {{ selectedItem.last_name }}.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click="dialogDelete = false">Cancelar</v-btn>
            <v-btn color="error" :loading="saving" @click="deleteTransportista">Eliminar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import '@/plugins/axios'
import Vue from 'vue'
import VuejsClipper from 'vuejs-clipper'
import ClipperFixed from 'vuejs-clipper/src/components/clipper-fixed'
import store from '@/store'
import { mapGetters } from 'vuex'

Vue.use(VuejsClipper, {
  components: {
    clipperBasic: 'clipper-basic',
    clipperFixed: 'clipper-fixed',
    clipperUpload: 'clipper-upload'
  }
})

export default {
  name: 'transportistas',
  components: {
    ClipperFixed
  },
  data: () => ({
    loading: false,
    saving: false,
    search: '',
    transportistas: [],
    dialogForm: false,
    dialogDelete: false,
    isEditing: false,
    selectedItem: {},
    headers: [
      { text: 'Nombre', value: 'first_name' },
      { text: 'Apellido', value: 'last_name' },
      { text: 'Telefono', value: 'telephone' },
      { text: 'Placas', value: 'plates' },
      { text: 'Unidad', value: 'unit_specs' },
      { text: 'Acciones', value: 'actions', sortable: false }
    ],
    form: {
      idEmpresa: null,
      first_name: '',
      last_name: '',
      telephone: '',
      plates: '',
      unit_specs: '',
      ine: null,
      resourceId: null
    },
    errors: {},
    inePreview: '',
    ineFileName: '',
    dialogCropIne: false,
    imageIneURL: '',
    imageIneObjectUrl: '',
    cropResultIneURL: '',
    cropDegrees: 0,
    cropRatio: 1.5,
    cropIneFileName: ''
  }),
  computed: {
    ...mapGetters(['providerHasParkingModule', 'getProviderParkingModule', 'getProfile']),
    providerCompanyId() {
      if (this.getProfile && this.getProfile.id_company) {
        return this.getProfile.id_company
      }
      if (this.getProviderParkingModule && this.getProviderParkingModule.provider_empresa_id) {
        return this.getProviderParkingModule.provider_empresa_id
      }
      return null
    }
  },
  created() {
    if (!this.providerHasParkingModule) {
      this.$store.dispatch('FETCH_PROVIDER_PARKING_MODULE')
        .then(() => this.fetchTransportistas())
        .catch(() => this.fetchTransportistas())
      return
    }
    this.fetchTransportistas()
  },
  methods: {
    clearTelephoneError() {
      if (this.errors && this.errors.telephone) {
        this.$delete(this.errors, 'telephone')
      }
    },
    validateMexPhone() {
      const digits = String(this.form.telephone || '').replace(/\D/g, '')
      let nationalNumber = ''

      if (digits.length === 13 && digits.startsWith('521')) {
        nationalNumber = digits.slice(3)
      } else if (digits.length === 12 && digits.startsWith('52')) {
        nationalNumber = digits.slice(2)
      } else if (digits.length === 10) {
        nationalNumber = digits
      }

      if (!nationalNumber || nationalNumber.length !== 10) {
        this.$set(this.errors, 'telephone', 'El telefono debe tener lada +52 y 10 digitos.')
        return false
      }

      this.form.telephone = '+52' + nationalNumber
      return true
    },
    normalizeRow(item) {
      return {
        ...item,
        resourceId: item.id || item.provider_id || item.pk || item.telephone
      }
    },
    fetchTransportistas() {
      if (!this.providerHasParkingModule) return

      this.loading = true
      const params = {}
      if (this.providerCompanyId) {
        params.idEmpresa = this.providerCompanyId
      }

      const config = {
        method: 'get',
        url: 'https://empresa-service.intrare.app/Parking/providers/',
        headers: {
          'Content-Type': 'application/json',
          authorization: 'Token ' + store.getters.getToken
        },
        params
      }

      axios(config)
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : []
          this.transportistas = rows.map(this.normalizeRow)
        })
        .catch((error) => {
          this.transportistas = []
          console.log(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    openCreateDialog() {
      this.isEditing = false
      this.dialogForm = true
      this.errors = {}
      this.form = {
        idEmpresa: this.providerCompanyId,
        first_name: '',
        last_name: '',
        telephone: '',
        plates: '',
        unit_specs: '',
        ine: null,
        resourceId: null
      }
      this.inePreview = ''
      this.ineFileName = ''
    },
    openEditDialog(item) {
      this.isEditing = true
      this.dialogForm = true
      this.errors = {}
      this.selectedItem = item
      this.form = {
        idEmpresa: this.providerCompanyId,
        first_name: item.first_name || '',
        last_name: item.last_name || '',
        telephone: item.telephone || '',
        plates: item.plates || '',
        unit_specs: item.unit_specs || '',
        ine: null,
        resourceId: item.resourceId
      }
      this.inePreview = item.ine || ''
      this.ineFileName = ''
    },
    closeDialog() {
      this.dialogForm = false
      this.errors = {}
      this.clearIne()
      this.dialogCropIne = false
    },
    pickIne() {
      if (this.$refs.ineInput) {
        this.$refs.ineInput.click()
      }
    },
    onInePicked(event) {
      const files = event.target.files
      if (!files || !files[0]) {
        return
      }

      const file = files[0]
      this.cropIneFileName = file.name
      if (this.imageIneObjectUrl) {
        URL.revokeObjectURL(this.imageIneObjectUrl)
      }
      this.imageIneObjectUrl = URL.createObjectURL(file)
      this.imageIneURL = this.imageIneObjectUrl
      this.cropDegrees = 0
      this.cropRatio = 1.5
      this.dialogForm = false
      this.$nextTick(() => {
        this.dialogCropIne = true
      })
    },
    cancelCropIne() {
      this.dialogCropIne = false
      this.imageIneURL = ''
      this.cropIneFileName = ''
      if (this.imageIneObjectUrl) {
        URL.revokeObjectURL(this.imageIneObjectUrl)
        this.imageIneObjectUrl = ''
      }
      if (this.$refs.ineInput) {
        this.$refs.ineInput.value = ''
      }
      this.dialogForm = true
    },
    applyIneCrop() {
      if (!this.$refs.clipperIne) {
        return
      }
      const canvas = this.$refs.clipperIne.clip()
      this.cropResultIneURL = canvas.toDataURL('image/jpeg', 0.85)
      this.form.ine = this.dataURLtoFile(this.cropResultIneURL, this.cropIneFileName || 'ine.jpeg')
      this.inePreview = this.cropResultIneURL
      this.ineFileName = this.cropIneFileName || 'ine.jpeg'
      this.dialogCropIne = false
      this.imageIneURL = ''
      this.cropIneFileName = ''
      if (this.imageIneObjectUrl) {
        URL.revokeObjectURL(this.imageIneObjectUrl)
        this.imageIneObjectUrl = ''
      }
      this.dialogForm = true
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    clearIne() {
      this.form.ine = null
      this.ineFileName = ''
      this.cropResultIneURL = ''
      this.imageIneURL = ''
      this.cropIneFileName = ''
      if (this.imageIneObjectUrl) {
        URL.revokeObjectURL(this.imageIneObjectUrl)
        this.imageIneObjectUrl = ''
      }
      if (!this.isEditing) {
        this.inePreview = ''
      }
      if (this.$refs.ineInput) {
        this.$refs.ineInput.value = ''
      }
    },
    buildFormData() {
      const bodyFormData = new FormData()
      if (this.providerCompanyId) {
        bodyFormData.set('idEmpresa', this.providerCompanyId)
      }
      bodyFormData.set('first_name', this.form.first_name)
      bodyFormData.set('last_name', this.form.last_name)
      bodyFormData.set('telephone', this.form.telephone)
      bodyFormData.set('plates', this.form.plates)
      bodyFormData.set('unit_specs', this.form.unit_specs)
      if (this.form.ine) {
        bodyFormData.append('ine', this.form.ine)
      }
      return bodyFormData
    },
    saveTransportista() {
      this.errors = {}
      if (!this.validateMexPhone()) {
        return
      }
      this.saving = true

      const bodyFormData = this.buildFormData()
      const url = this.isEditing
        ? 'https://empresa-service.intrare.app/Parking/providers/' + this.form.resourceId + '/'
        : 'https://empresa-service.intrare.app/Parking/providers/'

      const config = {
        method: this.isEditing ? 'put' : 'post',
        url,
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }

      axios(config)
        .then(() => {
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.isEditing ? 'Transportista actualizado.' : 'Transportista creado.')
          this.dialogForm = false
          this.fetchTransportistas()
        })
        .catch((error) => {
          if (error.response && error.response.status === 400 && error.response.data) {
            this.errors = error.response.data
          }
        })
        .finally(() => {
          this.saving = false
        })
    },
    openDeleteDialog(item) {
      this.selectedItem = item
      this.dialogDelete = true
    },
    deleteTransportista() {
      this.saving = true
      const config = {
        method: 'delete',
        url: 'https://empresa-service.intrare.app/Parking/providers/' + this.selectedItem.resourceId + '/',
        headers: {
          'Content-Type': 'application/json',
          authorization: 'Token ' + store.getters.getToken
        }
      }

      axios(config)
        .then(() => {
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Transportista eliminado.')
          this.dialogDelete = false
          this.fetchTransportistas()
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped>
.transportista-clipper {
  min-height: 380px;
}

/* INE upload zone */
.ine-upload-zone {
  border: 2px dashed #1976d2;
  border-radius: 8px;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s;
  background: #f5f8ff;
}
.ine-upload-zone:hover {
  background: #e8f0fe;
}

/* INE preview card */
.ine-preview-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #1976d2;
}
.ine-preview-img {
  display: block;
}
.ine-preview-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(25, 118, 210, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  opacity: 0;
  transition: opacity 0.2s;
}
.ine-preview-card:hover .ine-preview-overlay {
  opacity: 1;
}
</style>
